class DirectVRMBoneModifier {
  constructor(originalArrayBuffer) {
    this.originalArrayBuffer = originalArrayBuffer;
    this.gltfData = null;
    this.vrmExtension = null;
    this.binaryData = null;
    this.boneRenameMap = new Map();
  }

  // Parse GLB file structure
  parseGLB() {
    const dataView = new DataView(this.originalArrayBuffer);
    
    // Validate GLB header
    const magic = dataView.getUint32(0, true);
    if (magic !== 0x46546C67) {
      throw new Error('Not a valid GLB file');
    }
    
    const version = dataView.getUint32(4, true);
    const length = dataView.getUint32(8, true);
    
    // Parse JSON chunk
    const jsonChunkLength = dataView.getUint32(12, true);
    const jsonChunkType = dataView.getUint32(16, true);
    
    if (jsonChunkType !== 0x4E4F534A) {
      throw new Error('Invalid JSON chunk');
    }
    
    const jsonStart = 20;
    const jsonBytes = new Uint8Array(this.originalArrayBuffer, jsonStart, jsonChunkLength);
    const jsonString = new TextDecoder().decode(jsonBytes);
    
    this.gltfData = JSON.parse(jsonString);
    this.vrmExtension = this.gltfData.extensions?.VRM;
    
    // Parse binary chunk if exists
    const binaryStart = jsonStart + jsonChunkLength;
    if (binaryStart < length) {
      const binaryChunkLength = dataView.getUint32(binaryStart, true);
      const binaryChunkType = dataView.getUint32(binaryStart + 4, true);
      
      if (binaryChunkType === 0x004E4942) { // "BIN\0"
        this.binaryData = new Uint8Array(this.originalArrayBuffer, binaryStart + 8, binaryChunkLength);
      }
    }
    
    console.log('Parsed GLB:', {
      version,
      totalLength: length,
      jsonLength: jsonChunkLength,
      binaryLength: this.binaryData?.length || 0,
      nodeCount: this.gltfData.nodes?.length || 0,
      hasVRM: !!this.vrmExtension
    });
    
    return this.gltfData;
  }

  // Analyze current bone structure
  analyzeBones() {
    if (!this.gltfData?.nodes) return { bones: [], renamedBones: [], unmappedBones: [] };
    
    const bones = [];
    const renamedBones = [];
    const unmappedBones = [];
    
    this.gltfData.nodes.forEach((node, index) => {
      // Check if this is likely a bone (has bone-like name or is referenced in humanoid)
      const isBone = this.isBoneNode(node, index);
      
      if (isBone) {
        bones.push({ index, name: node.name, node });
        
        // Check if this bone should be renamed
        const standardName = this.findStandardBoneName(node.name);
        if (standardName && standardName !== node.name) {
          renamedBones.push({
            index,
            originalName: node.name,
            standardName,
            node
          });
          this.boneRenameMap.set(node.name, standardName);
        } else if (!standardName && this.looksLikeBone(node.name)) {
          unmappedBones.push({ index, name: node.name, node });
        }
      }
    });
    
    return { bones, renamedBones, unmappedBones };
  }

  // Check if a node is a bone
  isBoneNode(node, index) {
    // Check if referenced in VRM humanoid bones
    if (this.vrmExtension?.humanoid?.humanBones) {
      const isHumanoidBone = this.vrmExtension.humanoid.humanBones.some(
        humanBone => humanBone.node === index
      );
      if (isHumanoidBone) return true;
    }
    
    // Check if name looks like a bone
    return this.looksLikeBone(node.name);
  }

  // Check if name looks like a bone
  looksLikeBone(name) {
    const boneKeywords = [
      'hip', 'spine', 'chest', 'neck', 'head',
      'shoulder', 'arm', 'hand', 'finger', 'thumb',
      'leg', 'thigh', 'shin', 'foot', 'toe',
      'left', 'right', 'upper', 'lower'
    ];
    
    const lowerName = name.toLowerCase();
    return boneKeywords.some(keyword => lowerName.includes(keyword));
  }

  // Find standard bone name for a given name
  findStandardBoneName(originalName) {
    if (originalName.startsWith('mixamorig')) {
      return originalName.replace('mixamorig:', '').replace('mixamorig', '');
    }
    return null;
  }

  applyBoneRenaming() {
    if (!this.gltfData?.nodes) return { renamed: 0, humanoidUpdated: 0 };

    let renamedCount = 0;
    let humanoidUpdatedCount = 0;

    // Create a map of old node indices to new names for humanoid updates
    const nodeIndexToNewName = new Map();
    const oldToNewNameMap = new Map(); // Track old name -> new name mapping

    // Rename nodes and track changes - ONLY rename nodes that actually need it
    this.gltfData.nodes.forEach((node, index) => {
      const oldName = node.name;
      let newName = oldName;

      // Handle mixamo prefix removal
      if (oldName.includes('mixamorig')) {
        newName = oldName.replace('mixamorig:', '').replace('mixamorig', '');
      }

      // Only update if the name actually changed
      if (newName !== oldName) {
        console.log(`Renaming bone: "${oldName}" -> "${newName}"`);
        node.name = newName;
        nodeIndexToNewName.set(index, newName);
        oldToNewNameMap.set(oldName, newName);
        renamedCount++;
      }
    });

    // Update skeleton bone names in animations and other references
    if (oldToNewNameMap.size > 0) {
      this.updateSkeletonBoneNames(oldToNewNameMap);
    }

    // Update VRM humanoid bone references
    if (this.vrmExtension?.humanoid?.humanBones) {
      this.vrmExtension.humanoid.humanBones.forEach(humanBone => {
        const nodeIndex = humanBone.node;
        const node = this.gltfData.nodes[nodeIndex];

        if (node) {
          const newName = nodeIndexToNewName.get(nodeIndex);
          if (newName) {
            console.log(`Updating VRM humanoid bone "${humanBone.bone}": node[${nodeIndex}] "${node.name}" (renamed from previous)`);
            humanoidUpdatedCount++;
          } else {
            console.log(`VRM humanoid bone "${humanBone.bone}" points to node[${nodeIndex}] "${node.name}" (unchanged)`);
          }
        } else {
          console.warn(`VRM humanoid bone "${humanBone.bone}" points to invalid node index ${nodeIndex}`);
        }
      });
    }

    return { renamed: renamedCount, humanoidUpdated: humanoidUpdatedCount };
  }

  // Update skeleton bone names in animations and other references
  updateSkeletonBoneNames(oldToNewNameMap) {
    // Update animation channel targets
    if (this.gltfData.animations) {
      this.gltfData.animations.forEach((animation, animIndex) => {
        if (animation.channels) {
          animation.channels.forEach((channel, channelIndex) => {
            if (channel.target && channel.target.node !== undefined) {
              const nodeIndex = channel.target.node;
              const node = this.gltfData.nodes[nodeIndex];
              if (node && oldToNewNameMap.has(node.name)) {
                console.log(`Updated animation[${animIndex}].channel[${channelIndex}] target for bone "${node.name}"`);
              }
            }
          });
        }
      });
    }

    // Update any bone name references in VRM extensions
    if (this.vrmExtension) {
      // Update SpringBone bone references
      if (this.vrmExtension.secondaryAnimation?.boneGroups) {
        this.vrmExtension.secondaryAnimation.boneGroups.forEach((boneGroup, groupIndex) => {
          if (boneGroup.bones) {
            boneGroup.bones.forEach((boneIndex, boneArrayIndex) => {
              const node = this.gltfData.nodes[boneIndex];
              if (node && oldToNewNameMap.has(node.name)) {
                console.log(`Updated SpringBone group[${groupIndex}].bone[${boneArrayIndex}] reference for "${node.name}"`);
              }
            });
          }
        });
      }

      // Update any other VRM bone references that might exist
      if (this.vrmExtension.firstPerson?.meshAnnotations) {
        this.vrmExtension.firstPerson.meshAnnotations.forEach((annotation, annotationIndex) => {
          if (annotation.node !== undefined) {
            const node = this.gltfData.nodes[annotation.node];
            if (node && oldToNewNameMap.has(node.name)) {
              console.log(`Updated firstPerson meshAnnotation[${annotationIndex}] for "${node.name}"`);
            }
          }
        });
      }
    }

    console.log(`Updated skeleton references for ${oldToNewNameMap.size} renamed bones`);
  }

  addMissingBones() {
    const requiredBones = [
      { name: 'LeftHand_Gun', parent: 'LeftHand', position: [-0.076, -0.01, -0.013] },
      { name: 'RightHand_Gun', parent: 'RightHand', position: [0.076, -0.01, -0.013] }
    ];
  
    const existingBoneNames = this.gltfData.nodes.map(node => node.name);
    const missingBones = requiredBones.filter(bone => !existingBoneNames.includes(bone.name));
  
    let addedCount = 0;
  
    missingBones.forEach(missingBone => {
      // Find parent node index
      const parentIndex = this.gltfData.nodes.findIndex(node => node.name === missingBone.parent);
      
      if (parentIndex === -1) {
        console.warn(`Parent bone "${missingBone.parent}" not found for "${missingBone.name}"`);
        return;
      }
  
      // Find which skin contains the parent bone
      const skinIndex = this.findSkinIndexForBone(missingBone.parent);
      
      if (skinIndex === -1) {
        console.warn(`Parent bone "${missingBone.parent}" not found in any skin - new bone will be Object3D, not Bone`);
      }
  
      const newNode = {
        name: missingBone.name,
        translation: missingBone.position,
        rotation: [0, 0, 0, 1],
        scale: [1, 1, 1]
      };
  
      const nodeIndex = this.gltfData.nodes.length;
      this.gltfData.nodes.push(newNode);
  
      // Add to parent's children
      const parentNode = this.gltfData.nodes[parentIndex];
      if (!parentNode.children) {
        parentNode.children = [];
      }
      parentNode.children.push(nodeIndex);
  
      // Add to the same skin as the parent to make it a proper Bone
      if (skinIndex !== -1) {
        if (!this.gltfData.skins[skinIndex].joints) {
          this.gltfData.skins[skinIndex].joints = [];
        }
        this.gltfData.skins[skinIndex].joints.push(nodeIndex);
        console.log(`Added bone "${missingBone.name}" to skin[${skinIndex}] joints array - will be a Bone object`);
      } else {
        console.log(`Added node "${missingBone.name}" without skin reference - will be an Object3D`);
      }
  
      console.log(`Added missing bone: "${missingBone.name}" at index ${nodeIndex} as child of "${missingBone.parent}" (index ${parentIndex})`);
      addedCount++;
    });

    return { added: addedCount, missingBones };
  }
  
  findSkinIndexForBone(boneName) {
    if (!this.gltfData.skins) return -1;
  
    // First find the node index for the bone name
    const boneNodeIndex = this.gltfData.nodes.findIndex(node => node.name === boneName);
    if (boneNodeIndex === -1) {
      console.warn(`Bone "${boneName}" not found in nodes`);
      return -1;
    }

    // Find which skin contains this bone in its joints array
    for (let skinIndex = 0; skinIndex < this.gltfData.skins.length; skinIndex++) {
      const skin = this.gltfData.skins[skinIndex];
      if (skin.joints && skin.joints.includes(boneNodeIndex)) {
        console.log(`Found bone "${boneName}" in skin[${skinIndex}] at joint index ${skin.joints.indexOf(boneNodeIndex)}`);
        return skinIndex;
      }
    }

    console.warn(`Bone "${boneName}" not found in any skin joints`);
    return -1;
  }

  // Create modified VRM file
  createModifiedVRM() {
    // Reconstruct GLB file
    const jsonString = JSON.stringify(this.gltfData);
    const jsonBuffer = new TextEncoder().encode(jsonString);
    
    // Pad JSON to 4-byte boundary
    const jsonPadding = (4 - (jsonBuffer.length % 4)) % 4;
    const paddedJsonLength = jsonBuffer.length + jsonPadding;
    
    // Calculate sizes
    const headerSize = 12;
    const jsonChunkHeaderSize = 8;
    const binaryChunkHeaderSize = this.binaryData ? 8 : 0;
    const binaryDataSize = this.binaryData ? this.binaryData.length : 0;
    const totalSize = headerSize + jsonChunkHeaderSize + paddedJsonLength + binaryChunkHeaderSize + binaryDataSize;
    
    // Create new buffer
    const glbBuffer = new ArrayBuffer(totalSize);
    const dataView = new DataView(glbBuffer);
    const uint8View = new Uint8Array(glbBuffer);
    
    let offset = 0;
    
    // GLB header
    dataView.setUint32(offset, 0x46546C67, true); // "glTF"
    offset += 4;
    dataView.setUint32(offset, 2, true); // version
    offset += 4;
    dataView.setUint32(offset, totalSize, true); // length
    offset += 4;
    
    // JSON chunk header
    dataView.setUint32(offset, paddedJsonLength, true);
    offset += 4;
    dataView.setUint32(offset, 0x4E4F534A, true); // "JSON"
    offset += 4;
    
    // JSON data
    uint8View.set(jsonBuffer, offset);
    offset += jsonBuffer.length;
    
    // JSON padding
    for (let i = 0; i < jsonPadding; i++) {
      uint8View[offset + i] = 0x20; // space
    }
    offset += jsonPadding;
    
    // Binary chunk (if exists)
    if (this.binaryData) {
      dataView.setUint32(offset, binaryDataSize, true);
      offset += 4;
      dataView.setUint32(offset, 0x004E4942, true); // "BIN\0"
      offset += 4;
      uint8View.set(this.binaryData, offset);
    }
    
    const blob =  new Blob([glbBuffer], { type: 'model/gltf-binary' });

    return blob;
  }

  // Main processing function
  processVRM() {
    console.log('Starting direct VRM bone processing...');
    
    // Parse the file
    this.parseGLB();
    
    // Analyze bones
    const analysis = this.analyzeBones();
    console.log('Bone analysis:', analysis);
    
    // Apply renaming
    const renameResult = this.applyBoneRenaming();
    console.log('Rename result:', renameResult);
    
    // Add missing bones
    const addResult = this.addMissingBones();
    console.log('Add bones result:', addResult);
    
    return {
      totalBones: analysis.bones.length,
      renamedBones: analysis.renamedBones.length,
      addedBones: addResult.added,
      analysis,
      renameResult,
      addResult
    };
  }
}

export default DirectVRMBoneModifier;
